import {
    Component,
    computed,
    effect,
    inject,
    input,
    NgZone,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { isEqual } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { FtPodiumComponent } from './ft-podium.component';

/**
 * Native port of one podium "slot" — the DOM the AngularJS `ftPodiumPhoto` /
 * `ftPodiumWinner` / `ftWinnerCaption` directives produced around an `<ft-image>` in the
 * winners sheet. DISPLAY mode is render-only; INTERACTIVE mode (`setWinnerFinalist` +
 * `podiumRef`) wires the `ftSetWinner` click behavior with the Replace/Bump/Swap
 * concurrency dropdown. `ViewEncapsulation.None` for the global podium styles.
 */
@Component({
    selector: 'ft-podium-slot-ng',
    imports: [],
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./ft-podium-slot.component.scss'],
    templateUrl: './ft-podium-slot.component.html',
    host: {
        '[class.ng-hide]': 'isHidden()',
        '(click)': 'onSlotClick($event)',
        '(document:click)': 'onDocumentClick()',
    }
})
export class FtPodiumSlotComponent {
    /** The winner photo object, or `null` (incentive defined, no winner) / `undefined` (no incentive). */
    readonly winner = input<any>();
    /** The pre-resolved photo URL (`winner | ftAzureStoredPhoto:size`) — parent computes it via the bridged filter. */
    readonly src = input<string | null>();
    /** 'first' | 'second' | 'third' | 'runner'. */
    readonly placeType = input.required<string>();
    /** 'normal' (big podium) | 'small' (mini editor podium) | undefined (runner-ups have no caption). */
    readonly captionType = input<'normal' | 'small'>();

    /** INTERACTIVE mode (`ft-set-winner="model.finalist"`): the photo being ranked. */
    readonly setWinnerFinalist = input<any>();
    /** The FtPodiumComponent controller instance (passed via a `#podium` template ref). */
    readonly podiumRef = input<FtPodiumComponent>();
    /** Runner-up `$index` (for setRunner). */
    readonly runnerIndex = input<number>();

    protected readonly backgroundImage = signal<string | null>(null);
    /** always-visible: `.ft-image-div` is shown regardless of whether the photo exists. */
    protected readonly isVisible = true;
    /** Concurrency dropdown (Replace/Bump/Swap) visibility. */
    protected readonly isMenuVisible = signal(false);

    private readonly zone = inject(NgZone);
    private readonly translate: (key: string) => string;
    private static readonly CAPTIONS: { [type: string]: { [place: string]: string } } = {
        small: {
            first: 'flipto.account.sections.platform.photoAdvocacy.place.First',
            second: 'flipto.account.sections.platform.photoAdvocacy.place.Second',
            third: 'flipto.account.sections.platform.photoAdvocacy.place.Third',
            runner: 'flipto.account.sections.platform.photoAdvocacy.RunnerUp',
        },
        normal: {
            first: 'flipto.account.sections.platform.photoAdvocacy.1STPlace',
            second: 'flipto.account.sections.platform.photoAdvocacy.2NDPlace',
            third: 'flipto.account.sections.platform.photoAdvocacy.3RDPlace',
            runner: 'flipto.account.sections.platform.photoAdvocacy.RunnerUp',
        },
    };

    constructor() {
        const translateSvc = inject(TranslateService);
        this.translate = (key: string, params?: any) => translateSvc.instant(key, params);
        effect(() => this.loadImage(this.src()));
    }

    /** `ftPodiumWinner`: visible when `angular.isDefined(winner)` (null counts as defined). */
    protected readonly isHidden = computed<boolean>(() => this.winner() === undefined);

    protected readonly captionLabel = computed<string>(() => {
        const key = FtPodiumSlotComponent.CAPTIONS[this.captionType() as string]?.[this.placeType()];
        return key ? this.translate(key) : '';
    });

    private get isInteractive(): boolean {
        return typeof this.setWinnerFinalist() !== 'undefined' && !!this.podiumRef();
    }

    protected tr(key: string): string {
        return this.translate(key);
    }

    // ---- ftSetWinner (interactive) ----

    /** v1 `elem.on('click')` on the slot. */
    protected onSlotClick(e: Event): void {
        if (!this.isInteractive) return;
        e.stopPropagation();
        if (this.podiumRef()!.isEditable() === false) return;
        const finalist = this.setWinnerFinalist();
        // same slot → remove the finalist.
        if (isEqual(this.winner(), finalist)) {
            this.podiumRef()!.remove(finalist);
        } else if (this.placeType() === 'runner') {
            this.setRunner();
        } else {
            this.setWinner();
        }
    }

    /** v1 `$document.on('click')` — hide the concurrency menu on any outside click. */
    protected onDocumentClick(): void {
        if (this.isMenuVisible()) this.isMenuVisible.set(false);
    }

    private setWinner(): void {
        const finalist = this.setWinnerFinalist();
        const podium = this.podiumRef()!;
        if (podium.isPlaceTaken(this.placeType()) === false) {
            podium.remove(finalist);
            podium.setWinner(finalist, this.placeType());
        } else if (podium.isPlaceTakenByFinalist(finalist, this.placeType()) === false) {
            this.isMenuVisible.set(true);
        }
    }

    private setRunner(): void {
        this.podiumRef()!.remove(this.setWinnerFinalist());
        this.podiumRef()!.setRunner(this.setWinnerFinalist(), this.runnerIndex() as number);
    }

    protected replaceCurrent(e: Event): void {
        e.stopPropagation();
        this.isMenuVisible.set(false);
        this.podiumRef()!.remove(this.setWinnerFinalist());
        this.podiumRef()!.setWinner(this.setWinnerFinalist(), this.placeType());
    }

    protected bumpCurrentDown(e: Event): void {
        e.stopPropagation();
        this.isMenuVisible.set(false);
        this.podiumRef()!.remove(this.setWinnerFinalist());
        this.podiumRef()!.bumpDown(this.setWinnerFinalist(), this.placeType());
    }

    protected swapWithCurrent(e: Event): void {
        e.stopPropagation();
        this.isMenuVisible.set(false);
        this.podiumRef()!.swap(this.winner(), this.setWinnerFinalist());
    }

    private loadImage(val: string | null | undefined): void {
        this.backgroundImage.set(null);
        if (!val) return;
        let image: HTMLImageElement | null = new Image();
        image.onload = () => {
            this.zone.run(() => {
                this.backgroundImage.set(`url(${val})`);
                image = null;
            });
        };
        image.onerror = () => {
            this.zone.run(() => {
                this.backgroundImage.set(null);
                image = null;
            });
        };
        image.src = val;
    }
}
